// DocumentDetailsModal.js - Fetch single document
import React, { useState, useEffect } from 'react';
import appStyle from "./DataIdModal.module.css";

const getToken = () => localStorage.getItem("token");
const backend_api_base_url = `${process.env.REACT_APP_SL_BE_BASE_ENV_PART}/${process.env.REACT_APP_STAGE_ENV}`;

const DocumentDetailsModal = ({ dataId, kbId, region, onClose }) => {
  const [documentData, setDocumentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDocument = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = getToken();

        const response = await fetch(
          `https://${region}.autobot2.${backend_api_base_url}/ai_ingest_sys?task=get_document&kb_id=${kbId}&data_id=${dataId}`,
          {
            method: "GET",
            headers: {
              Authorization: `${token}`,
              "Content-Type": "application/json",
            }
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status} for ID ${dataId}`);
        }

        const result = await response.json();

        // Handle different response formats
        let documentData = null;

        // Check if the response has the new format: { statusCode, headers, body: { status, message, data } }
        if (result.body && typeof result.body === 'object') {
          // New format: body contains the actual data
          if (result.body.status === 'ok' && result.body.data) {
            documentData = {
              status: 'ok',
              data: result.body.data
            };
          } else if (result.body.data) {
            // If body has data directly without status
            documentData = {
              status: 'ok',
              data: result.body.data
            };
          } else {
            // If body has some other structure
            documentData = result.body;
          }
        } else if (result.status === 'ok' && result.data) {
          // Old format: direct status and data
          documentData = result;
        } else if (result.data) {
          // If data is directly in the response
          documentData = {
            status: 'ok',
            data: result.data
          };
        } else {
          // If response is the data itself
          documentData = {
            status: 'ok',
            data: result
          };
        }

        // Check if we have valid data
        if (documentData && documentData.data) {
          setDocumentData(documentData);
        } else {
          setError(documentData?.message || documentData?.error || 'Document not found or invalid format');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (dataId && region) {
      fetchDocument();
    }
  }, [dataId, kbId, region]);

  // Extract only URL and chunk from the response
  const extractDocumentInfo = (document) => {
    if (!document) {
      return { url: 'N/A', data: 'No data available' };
    }

    // If document has status 'ok' and data
    if (document.status === 'ok' && document.data) {
      return {
        url: document.data.url || 'N/A',
        data: document.data.chunk || 'No content available'
      };
    }

    // If document is directly the data object
    if (document.url || document.chunk) {
      return {
        url: document.url || 'N/A',
        data: document.chunk || 'No content available'
      };
    }

    // If document has data property with url/chunk
    if (document.data && (document.data.url || document.data.chunk)) {
      return {
        url: document.data.url || 'N/A',
        data: document.data.chunk || 'No content available'
      };
    }

    return { url: 'N/A', data: 'No data available' };
  };

  return (
    <div className={appStyle["modal-overlay"]} onClick={onClose}>
      <div className={appStyle["modal"]} onClick={(e) => e.stopPropagation()}>
        <div className={appStyle["modal-header"]}>
          <h3>Document Details: {dataId}</h3>
          <button className={appStyle["close-button"]} onClick={onClose}>×</button>
        </div>

        <div className={appStyle["modal-content"]}>
          {loading && (
            <div className={appStyle["loading"]}>
              Loading document details...
            </div>
          )}

          {error && (
            <div className={appStyle["error"]}>
              Error fetching document: {error}
            </div>
          )}

          {!loading && !error && documentData && (
            <div className={appStyle["document-item"]}>
              <div className={appStyle["document-info"]}>
                <div className={appStyle["info-row"]}>
                  <strong>Document ID:</strong>
                  <span className={appStyle["info-value"]}>
                    {dataId}
                  </span>
                </div>
                <div className={appStyle["info-row"]}>
                  <strong>URL:</strong>
                  <span className={appStyle["info-value"]}>
                    {extractDocumentInfo(documentData).url}
                  </span>
                </div>
                {documentData.data?.title && (
                  <div className={appStyle["info-row"]}>
                    <strong>Title:</strong>
                    <span className={appStyle["info-value"]}>
                      {documentData.data.title}
                    </span>
                  </div>
                )}
                {documentData.data?.content_type && (
                  <div className={appStyle["info-row"]}>
                    <strong>Content Type:</strong>
                    <span className={appStyle["info-value"]}>
                      {documentData.data.content_type}
                    </span>
                  </div>
                )}
                {documentData.data?.chunk_id !== undefined && (
                  <div className={appStyle["info-row"]}>
                    <strong>Chunk ID:</strong>
                    <span className={appStyle["info-value"]}>
                      {documentData.data.chunk_id}
                    </span>
                  </div>
                )}
                <div className={appStyle["info-row"]}>
                  <strong>Content:</strong>
                  <div className={appStyle["data-content"]}>
                    {extractDocumentInfo(documentData).data}
                  </div>
                </div>
              </div>
            </div>
          )}

          {!loading && !error && !documentData && (
            <div className={appStyle["no-data"]}>
              No document data available
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentDetailsModal;